import React from 'react';
import AutoReleaseCountdown from './AutoReleaseCountdown';
import { useFees } from '../../context/FeeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ClientConfirmation = ({
  booking,
  onConfirmCompletion,
  onShowDisputeForm,
  showDisputeForm,
  loading
}) => {
  const { feeRates } = useFees();
  return (
    <div className="space-y-4">
      {booking.completion_requested_at && (
        <AutoReleaseCountdown requestedAt={booking.completion_requested_at} variant="client" hours={feeRates.auto_confirm_hours} />
      )}
      <div className="bg-purple-50 p-4 rounded-lg">
        <h4 className="font-semibold text-purple-700 mb-2">Service Provider has marked this job as complete!</h4>
        {booking.completion_notes && (
          <p className="text-gray-600 text-sm mb-2">Notes: {booking.completion_notes}</p>
        )}
        
        {/* Display Proof Images */}
        {booking.proof_images && booking.proof_images.length > 0 && (
          <div className="mt-3 pt-3 border-t border-purple-200">
            <p className="text-sm font-medium text-purple-700 mb-2">Proof of Completion ({booking.proof_images.length} photos):</p>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
              {booking.proof_images.map((img, index) => (
                <img
                  key={index}
                  src={img.startsWith('http') ? img : `${BACKEND_URL}${img}`}
                  alt={`Proof ${index + 1}`}
                  className="w-full h-20 object-cover rounded-lg border border-purple-200 cursor-pointer hover:opacity-80 transition"
                  onClick={() => window.open(img.startsWith('http') ? img : `${BACKEND_URL}${img}`, '_blank')}
                  data-testid={`client-proof-${index}`}
                />
              ))}
            </div>
            <p className="text-xs text-gray-500 mt-2">Click on any photo to view full size</p>
          </div>
        )}

        {/* Display Proof Files (non-image deliverables) */}
        {booking.proof_files && booking.proof_files.length > 0 && (
          <div className="mt-3 pt-3 border-t border-purple-200" data-testid="client-proof-files">
            <p className="text-sm font-medium text-purple-700 mb-2 flex items-center gap-2">
              Deliverable Files ({booking.proof_files.length}):
              {booking.proof_locked && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full" title="Final deliverables — locked after client confirmation">
                  🔒 Locked (final)
                </span>
              )}
            </p>
            <ul className="space-y-2">
              {booking.proof_files.map((f, idx) => {
                const href = f.url?.startsWith('http') ? f.url : `${BACKEND_URL}${f.url}`;
                const sizeLabel = f.size_bytes != null
                  ? f.size_bytes < 1024 * 1024
                    ? `${(f.size_bytes / 1024).toFixed(1)} KB`
                    : `${(f.size_bytes / (1024 * 1024)).toFixed(1)} MB`
                  : '';
                return (
                  <li
                    key={idx}
                    className="flex items-center gap-2 bg-white border border-purple-200 rounded px-3 py-2"
                    data-testid={`client-proof-file-${idx}`}
                  >
                    <span className="text-xl">📎</span>
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      download={f.original_name}
                      className="flex-1 truncate text-sm font-medium text-gray-800 hover:text-purple-700"
                      title={f.original_name}
                    >
                      {f.original_name || 'deliverable'}
                    </a>
                    <span className="text-xs text-gray-400">{sizeLabel}</span>
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      download={f.original_name}
                      className="text-xs text-purple-600 hover:text-purple-800 font-medium"
                    >
                      Download
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>

      <button
        onClick={onConfirmCompletion}
        disabled={loading}
        className="w-full bg-green-600 text-white py-3 rounded-lg hover:bg-green-700 font-semibold disabled:opacity-50"
        data-testid="confirm-completion-btn"
      >
        {loading ? 'Processing...' : 'Confirm Completion & Release Payment'}
      </button>

      <div className="border-t pt-4">
        <button
          onClick={onShowDisputeForm}
          className="flex items-center gap-2 text-red-600 hover:text-red-700 font-semibold"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          Not satisfied with the work?
        </button>
      </div>
    </div>
  );
};

export default ClientConfirmation;
